/**
 * 锁定参数时间轴组件
 * 展示养护流程但隐藏关键参数
 * Requirements: 5.1, 5.2, 5.3, 5.5, 6.1, 6.2, 6.3, 6.4, 6.5
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  Lock,
  Unlock,
  CheckCircle,
  Clock,
  Calendar,
  ChevronRight,
  Wrench,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LockedParamDisplay } from "./LockedParamTooltip";
import type {
  LockedTimelineProps,
  Day0Task,
  Milestone,
} from "@/types/conversion-funnel";

/**
 * 任务项组件
 */
interface TaskItemProps {
  task: Day0Task;
  isUnlocked: boolean;
}

function TaskItem({ task, isUnlocked }: TaskItemProps) {
  const { t } = useTranslation("assessments");

  return (
    <div className="flex items-start gap-3 p-3 bg-card rounded-lg border border-border">
      {/* 任务图标 */}
      <div
        className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          isUnlocked
            ? "bg-emerald-100 dark:bg-emerald-900/30"
            : "bg-amber-100 dark:bg-amber-900/30"
        )}
      >
        {isUnlocked ? (
          <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        )}
      </div>

      {/* 任务内容 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground">
            {task.action}
          </span>
          {task.toolbox_item_used && (
            <Badge variant="secondary" className="text-xs">
              <Wrench className="h-3 w-3 mr-1" />
              {task.toolbox_item_used}
            </Badge>
          )}
        </div>

        {/* 参数显示 */}
        {task.param_unit && (
          <div className="mt-2">
            {isUnlocked && task.param_value !== null ? (
              <Badge
                variant="outline"
                className="bg-emerald-100/50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
              >
                <Unlock className="h-3 w-3 mr-1" />
                {task.param_value}
                {task.param_unit}
              </Badge>
            ) : (
              <LockedParamDisplay
                unit={task.param_unit}
                lockReason={task.lock_reason}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 关键节点组件
 */
interface MilestoneItemProps {
  milestone: Milestone;
  isUnlocked: boolean;
  isLast: boolean;
}

function MilestoneItem({ milestone, isUnlocked, isLast }: MilestoneItemProps) {
  const { t } = useTranslation("assessments");

  return (
    <div className="relative">
      {/* 连接线 */}
      {!isLast && (
        <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-border" />
      )}

      <div className="flex gap-4">
        {/* 天数标记 */}
        <div
          className={cn(
            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold",
            milestone.day === 0
              ? "bg-destructive/10 text-destructive dark:bg-destructive/20"
              : "bg-muted text-muted-foreground"
          )}
        >
          D{milestone.day}
        </div>

        {/* 内容 */}
        <div className="flex-1 pb-6">
          <h4 className="text-sm font-semibold text-foreground mb-2">
            {milestone.title}
          </h4>

          {/* 摘要（无具体任务的阶段） */}
          {milestone.summary && !milestone.tasks?.length && (
            <p className="text-sm text-muted-foreground">{milestone.summary}</p>
          )}

          {/* 具体任务 */}
          {milestone.tasks && milestone.tasks.length > 0 && (
            <div className="space-y-2">
              {milestone.tasks.map((task, index) => (
                <TaskItem key={index} task={task} isUnlocked={isUnlocked} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 阶段进度指示器
 */
interface PhaseProgressProps {
  phases: string[];
  totalDays: number;
}

function PhaseProgress({ phases, totalDays }: PhaseProgressProps) {
  const { t } = useTranslation("assessments");

  return (
    <Card className="p-3 sm:p-4">
      {/* 总天数 */}
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {t("conversion.timeline.totalDays", "{{days}}天养护周期", {
            days: totalDays,
          })}
        </span>
      </div>

      {/* 阶段进度 */}
      <div className="flex items-center gap-1 flex-wrap">
        {phases.map((phase, index) => (
          <React.Fragment key={index}>
            <Badge
              variant={index === 0 ? "destructive" : "secondary"}
              className={cn(
                index === 0 &&
                  "bg-destructive/10 text-destructive hover:bg-destructive/20 dark:bg-destructive/20"
              )}
            >
              {phase}
            </Badge>
            {index < phases.length - 1 && (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
}

/**
 * 锁定参数时间轴
 * 展示 Day 0 任务、关键节点和养护周期概览
 */
export function LockedTimeline({
  timelinePreview,
  isUnlocked,
  onUnlock,
  className,
}: LockedTimelineProps) {
  const { t } = useTranslation("assessments");
  const { day_0_tasks, total_days, phases, milestones } = timelinePreview;

  return (
    <div className={cn("space-y-3 sm:space-y-4", className)}>
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            {t("conversion.timeline.title", "养护时间轴")}
          </h3>
        </div>
        {!isUnlocked && (
          <Badge
            variant="outline"
            className="bg-amber-100/50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800"
          >
            <Lock className="h-3 w-3 mr-1" />
            {t("conversion.timeline.locked", "部分参数已锁定")}
          </Badge>
        )}
      </div>

      {/* 阶段进度 */}
      <PhaseProgress phases={phases} totalDays={total_days} />

      {/* 关键节点时间轴 */}
      <Card className="p-3 sm:p-4">
        <h4 className="text-sm font-medium text-muted-foreground mb-4">
          {t("conversion.timeline.milestones", "关键节点")}
        </h4>
        <div className="space-y-0">
          {milestones.map((milestone, index) => (
            <MilestoneItem
              key={index}
              milestone={milestone}
              isUnlocked={isUnlocked}
              isLast={index === milestones.length - 1}
            />
          ))}
        </div>
      </Card>

      {/* 解锁提示（仅在锁定状态显示） */}
      {!isUnlocked && (
        <Card className="p-3 sm:p-4 bg-amber-50/50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
          <div className="flex items-start gap-2 sm:gap-3">
            <Lock className="h-5 w-5 text-amber-500 dark:text-amber-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {t("conversion.timeline.unlockHint", "解锁后查看精准参数")}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                {t(
                  "conversion.timeline.unlockDescription",
                  "所有参数均基于您的鱼缸体积和鱼种特性精准计算"
                )}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default LockedTimeline;
